import { IImageStyle, IImageWidgetData, IImageWidgetStyle, ILinkData, ImageWidgetData } from "@pageBuilder/typings";
import { IRenderingCommonContext } from "@commonRendering/types";
import { CssProperties } from "../../../models/css-property";
import { CssUtil } from "../../../utils/css-util";
import { LinkRenderer } from "../link.renderer";
import { WidgetRendererBase } from "../widget-renderer-base";
import { ImageScriptRenderer } from "./image-script.renderer";

export class ImageWidgetRenderer extends WidgetRendererBase<IImageWidgetData> {

    private readonly linkRenderer: LinkRenderer;
    private readonly scriptRenderer: ImageScriptRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);

        this.linkRenderer = new LinkRenderer();
        this.scriptRenderer = new ImageScriptRenderer();
    }

    protected createNewInternal(): HTMLElement {
        const element = this.createWidgetElement("imageWidget");
        element.classList.add("aspect-ratio-resizable");

        return element;
    }

    protected renderInternal(element: HTMLElement, data: IImageWidgetData): HTMLElement {
        if (data.src) {
            this.removePlaceholder(element);
            this.renderLink(element, data.linkData);
            this.renderImage(element, data);
        } else {
            this.removeLink(element);
            this.removeImage(element);
            this.renderPlaceholder(element);
            this.renderLink(element, data.linkData);
        }

        this.renderStyles(element, data);

        return element;
    }

    private removePlaceholder(element: HTMLElement): void {
        const notAvailableElement = element.querySelector<HTMLDivElement>(".not-available");
        notAvailableElement?.remove();
    }

    private renderImage(element: HTMLElement, data: IImageWidgetData): void {
        let img = element.querySelector<HTMLImageElement>("img");
        if (!img) {
            img = document.createElement("img");
            img.classList.add("image");
        }

        img.setAttribute("draggable", "false");
        img.setAttribute("aspect-ratio", `auto${data.aspectRatio ? ` ${data.aspectRatio}` : ""}`);

        if (img.getAttribute("src") !== data.src) {
            img.src = data.src || "";
        }
        img.alt = data.altText || "";

        const imageContainer = element.querySelector(".widget-content > a") || element.querySelector(".widget-content");
        imageContainer?.appendChild(img);
    }

    private removeImage(element: HTMLElement): void {
        const img = element.querySelector<HTMLImageElement>("img");
        img?.remove();
    }

    private renderPlaceholder(element: HTMLElement): void {
        const content = element.querySelector(".widget-content") as HTMLElement;
        let notAvailableElement = element.querySelector<HTMLDivElement>(".not-available");
        if (!notAvailableElement) {
            notAvailableElement = document.createElement("div");
            notAvailableElement.classList.add("not-available");
            content.appendChild(notAvailableElement);
        }
    }

    private renderLink(element: HTMLElement, data: ILinkData | undefined): void {
        if (!data || data.type === "none") {
            return;
        }
        const content = element.querySelector(".widget-content") as HTMLElement;
        let link = element.querySelector<HTMLAnchorElement>("a");
        if (!link) {
            link = document.createElement("a");
            content.appendChild(link);
        }
        link.setAttribute("tabindex", "-1");

        this.linkRenderer.render(link, data);
    }

    private removeLink(element: HTMLElement): void {
        const link = element.querySelector<HTMLAnchorElement>("a");
        link?.remove();
    }

    protected renderStyles(element: HTMLElement, data: IImageWidgetData): void {
        this.renderBaseStyles(element, data.style);

        if (!data.style?.imageStyle) {
            this.cleanupStyle(element);
            return;
        }

        this.renderImageStyles(element, data.style.imageStyle);
        this.renderContentStyles(element, data.style.imageStyle);
        this.renderScripts(element);
    }

    private renderBaseStyles(element: HTMLElement, style: IImageWidgetStyle | null): void {
        this.renderHeightStyles(element, style);
        this.animationRenderer.setAnimationStyles(element, style?.animation);
    }

    private renderHeightStyles(element: HTMLElement, style: IImageWidgetStyle | null): void {
        if (style?.heightData?.heightFixed) {
            if (element.style.paddingTop !== "initial") {
                element.style.height = style.heightData.height
                    ? style.heightData.height + "px"
                    : element.clientHeight.toString();
                element.style.paddingTop = "initial";
            }
            element.classList.remove("aspect-ratio-resizable");
        } else {
            const paddingTop= element.style.paddingTop === "initial"
                ? (element.clientHeight / element.clientWidth * 100)
                : style?.heightData?.paddingTop
                ?? ImageWidgetData.defaultStyle.heightData?.paddingTop;
            element.style.paddingTop = `${paddingTop}%`;
            element.style.height = "initial";
            element.style.minHeight = "initial";
            element.classList.add("aspect-ratio-resizable");
        }
        if (style?.heightData && element.clientHeight) {
            style.heightData.height = element.clientHeight;
        }
    }

    private renderImageStyles(element: HTMLElement, style: IImageStyle): void {
        const imageProps = new CssProperties();
        const imageElement = element.querySelector<HTMLElement>(".image");
        const imageMainAspect = this.calculateImageMainAspect(element);

        const isContainMode = style.fitMode === "contain";
        imageProps.tryAdd("object-fit", isContainMode ? "contain" : "cover");

        if (imageMainAspect !== ImageMainAspect.None) {
            if (!isContainMode) {
                imageElement?.style.removeProperty("width");
                imageElement?.style.removeProperty("height");
            }

            const isNeedToFillByWidth = isContainMode && (imageMainAspect === ImageMainAspect.Width);
            const isNeedToFillByHeight = isContainMode && (imageMainAspect === ImageMainAspect.Height);
            imageProps.tryAdd("width", isNeedToFillByWidth ? "auto" : "100%");
            imageProps.tryAdd("height", isNeedToFillByHeight ? "auto" : "100%");
        } else {
            if (imageElement) {
                const currentWidth = CssUtil.getComputedStyle(imageElement)?.width;
                const currentHeight = CssUtil.getComputedStyle(imageElement)?.height;
                imageProps.tryAdd("width", currentWidth);
                imageProps.tryAdd("height", currentHeight);
            }
        }

        if (style.borderRadius) {
            imageProps.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            imageProps.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            imageProps.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            imageProps.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        if (style.position) {
            imageProps.tryAdd("object-position", `${style.position.offsetX}% ${style.position.offsetY}%`);
        }

        if (style.thickness) {
            imageProps.tryAdd("border-style", "solid");
            imageProps.tryAdd("border-width", style.thickness);
        }

        if (style.borderColor) {
            imageProps.tryAdd("border-color", `${style.borderColor}`);
        }

        this.styleService.setStyle(element, `#${element.id} .image`, imageProps.data);
    }

    private renderContentStyles(element: HTMLElement, style: IImageStyle): void {
        const contentProps = new CssProperties();

        contentProps.tryAdd("justify-content", style.imageAlignment);

        this.styleService.setStyle(element, `#${element.id} .widget-content`, contentProps.data);
    }

    private calculateImageMainAspect(element: HTMLElement): ImageMainAspect {
        const contentElement = element.querySelector<HTMLDivElement>(`#${element.id} .widget-content`);
        const imageElement = element.querySelector<HTMLImageElement>(`#${element.id} .image`);

        if (contentElement && imageElement) {
            if (contentElement.offsetWidth === 0 || contentElement.offsetHeight === 0 || imageElement.naturalWidth === 0 || imageElement.naturalHeight === 0) {
                return ImageMainAspect.None;
            }

            const contentAspectRatio = contentElement.offsetWidth / contentElement.offsetHeight;
            const imageAspectRatio = imageElement.naturalWidth / imageElement.naturalHeight;

            return contentAspectRatio >= imageAspectRatio ? ImageMainAspect.Width : ImageMainAspect.Height;
        }

        return ImageMainAspect.None;
    }

    private renderScripts(element: HTMLElement): void {
        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

    private cleanupStyle(element: HTMLElement): void {
        const selectors = this.getContentSelectorsInternal(element);
        if (selectors && selectors.length > 0) {
            selectors.forEach(selector => this.styleService.clearStyle(element, selector));
        }
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`, `#${element.id} .widget-content`, `#${element.id} .image`];
    }

}

enum ImageMainAspect {
    Width,
    Height,
    None
}
